/**
 * 营养相关API
 * 职责：对接后端营养标准、合规规则等接口
 */

import { request } from './client';
import { REQUIRED_NUTRIENTS, getStandardUnit } from '../config/nutrition.config';

/**
 * 获取营养相关合规规则
 * @returns {Promise<Array>} 合规规则列表
 */
export const fetchNutritionRules = async () => {
  return request('/compliance/rules', {
    method: 'GET',
    params: { ruleType: 'nutrition' }
  });
};

/**
 * 从合规规则中提取必填营养素列表
 * @returns {Promise<Array<string>>} 必填营养素名称列表
 */
export const getRequiredNutrientsFromRules = async () => {
  try {
    const rules = await fetchNutritionRules();
    
    // 查找营养成分表必填检查规则
    const requiredRule = rules.find(r => r.ruleCode === 'RULE_NUT_001');
    
    if (requiredRule && requiredRule.rulePattern) {
      try {
        const pattern = typeof requiredRule.rulePattern === 'string' 
          ? JSON.parse(requiredRule.rulePattern) 
          : requiredRule.rulePattern;
        
        if (pattern.requiredItems && Array.isArray(pattern.requiredItems)) {
          return pattern.requiredItems;
        }
      } catch (e) {
        console.warn('解析必填营养素规则失败:', e);
      }
    }
    
    // 如果无法从规则中获取，返回默认必填项
    return REQUIRED_NUTRIENTS;
  } catch (error) {
    console.error('获取必填营养素列表失败:', error);
    // 发生错误时返回默认必填项
    return REQUIRED_NUTRIENTS;
  }
};

/**
 * 从合规规则中提取营养素单位要求
 * @returns {Promise<Object>} 营养素单位映射 { 营养素名称: [允许的单位列表] }
 */
export const getNutrientUnitRequirements = async () => {
  try {
    const rules = await fetchNutritionRules();
    const unitMap = {};
    
    rules.forEach(rule => {
      if (rule.rulePattern) {
        try {
          const pattern = typeof rule.rulePattern === 'string' 
            ? JSON.parse(rule.rulePattern) 
            : rule.rulePattern;
          
          // RULE_NUT_002: 能量单位检查
          if (rule.ruleCode === 'RULE_NUT_002' && pattern.allowedUnits) {
            unitMap['能量'] = pattern.allowedUnits;
          }
          
          // RULE_NUT_003: 钠单位检查
          if (rule.ruleCode === 'RULE_NUT_003' && pattern.allowedUnits) {
            unitMap['钠'] = pattern.allowedUnits;
          }
        } catch (e) {
          console.warn(`解析规则${rule.ruleCode}失败:`, e);
        }
      }
    });
    
    return unitMap;
  } catch (error) {
    console.error('获取营养素单位要求失败:', error);
    return {};
  }
};

/**
 * 基于合规规则生成默认营养素列表
 * @returns {Promise<Array>} 默认营养素项数组
 */
export const generateDefaultNutritionItemsFromRules = async () => {
  try {
    // 从合规规则获取必填营养素列表
    const requiredNutrients = await getRequiredNutrientsFromRules();
    
    // 生成默认营养素项
    return requiredNutrients.map(name => ({
      name,
      per100g: '0',
      unit: getStandardUnit(name), // 使用标准单位
      nrv: '%',
      locked: false
    }));
  } catch (error) {
    console.error('从规则生成默认营养素列表失败:', error);
    // 发生错误时返回默认列表
    return REQUIRED_NUTRIENTS.map(name => ({
      name,
      per100g: '0',
      unit: getStandardUnit(name),
      nrv: '%',
      locked: false
    }));
  }
};

export default {
  fetchNutritionRules,
  getRequiredNutrientsFromRules,
  getNutrientUnitRequirements,
  generateDefaultNutritionItemsFromRules
};

